import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router, ActivationEnd, RouteConfigLoadStart, RouteConfigLoadEnd, NavigationCancel, NavigationStart, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { routeNames } from './app-routing-names';
import { DefaultDescriptionMetaTagContent, DefaultTitle, RetrieveDescriptionMetaTagContent, RetrieveTitle } from './constants/strings';
import { AuthenticationService } from './services/authentication/authentication.service';
import { InvalidTokenEventService } from './services/authentication/invalid-token-event.service';
import { GoogleTagManagerService } from './services/google-analytics/google-tag-manager.service';
import { LoadingIndicatorService } from './services/loading-indicator-service/loading-indicator.service';
import { LocalStoreManagerService } from './services/local-store-manager-service/local-store-manager.service';
import { ModalEventService } from './services/modal-service/modal-event.service';
import { ModalService } from './services/modal-service/modal.service';
import { UtilitiesService } from './services/utilities-service/utilities.service';
import { ToastNotificationService } from './services/toast-notification/toast-notification.service';
import { UnloadBrowserNotificationDirective } from './directives/unload-browser-notification.directive';
import { FooterComponent } from './components/footer/footer.component';
import { AuthType } from './models/enums/auth-type';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterOutlet, UnloadBrowserNotificationDirective, FooterComponent]
})
export class AppComponent implements OnInit, OnDestroy {
    private EventSubscriptionsArray: Array<Subscription> = [];

    constructor(public localStoreManager: LocalStoreManagerService,
        private router: Router,
        private modalEventService: ModalEventService,
        private modalService: ModalService,
        private invalidTokenEventService: InvalidTokenEventService,
        private authenticationService: AuthenticationService,
        private loadingService: LoadingIndicatorService,
        private googleTagManagerService: GoogleTagManagerService,
        private notificationService: ToastNotificationService,
        private meta: Meta,
        private title: Title) {
        localStoreManager.initialiseStorageSyncListener();
    }

    ngOnInit() {
        this.monitorModalEvents();
        this.monitorInvalidTokenEvents();
        this.monitorNavigationEndEvents();
        this.monitorPopstate();
        this.monitorLogin();
        (window as any).Twilio?.initWebchat();
        (window as any).Twilio?.openWebchatFAQs('Home');
    }

    ngOnDestroy(): void {
        this.EventSubscriptionsArray.forEach(sub => {
            UtilitiesService.safeUnsubscribe(sub);
        });
    }

    private monitorLogin() {
        this.authenticationService.onLogin().subscribe((loginType: AuthType | undefined) => {
            if (loginType === AuthType.External) {
                (globalThis as any).Twilio.initWebchat();
                (globalThis as any).Twilio?.openWebchatFAQs('Home')
            }
        })
    }
    
    private monitorModalEvents() {
        this.EventSubscriptionsArray.push(
            this.modalEventService.onOpenModalRequest()
                .subscribe(event => {
                    if (event && event.component) {
                        this.modalService.openModalComponent(event.component, event.inputs, event.inProcess, event.important, event.queue);
                        this.loadingService.hide();
                    }
                })
        );
    }

    private monitorInvalidTokenEvents() {
        this.EventSubscriptionsArray.push(
            this.invalidTokenEventService.onProcessInvalidToken()
                .subscribe(() => {
                    this.authenticationService.processInvalidSession();
                })
        );
    }

    private monitorNavigationEndEvents(): void {
        this.EventSubscriptionsArray.push(
            this.router.events.subscribe((r) => {
                if (r instanceof NavigationStart) {
                    this.loadingService.show();
                }
                if (r instanceof NavigationEnd) {
                    this.googleTagManagerService.track(r.url);
                    this.notificationService.closeSnackbar();
                    this.modalService.dismissAll();
                    this.loadingService.hide();
                }
                if (r instanceof ActivationEnd) {
                    this.updateMetaTagsAndTitle();
                }
                if (r instanceof RouteConfigLoadStart) { // loading screen for lazy loaded routes
                    this.loadingService.show();
                } else if (r instanceof RouteConfigLoadEnd) {
                    this.loadingService.hide();
                }
                else if (r instanceof NavigationCancel) {
                    this.loadingService.hide();
                }
            })
        );
    }

    private updateMetaTagsAndTitle(): void {
        const currentRoute = this.router.url;
        const currentDescriptionMetaTag: HTMLMetaElement = this.meta.getTag('name=description');
        const currentTitle = this.title.getTitle();

        if (currentDescriptionMetaTag) {
            if (currentRoute.includes(routeNames.QUOTE_RETRIEVAL)) {
                if (currentDescriptionMetaTag?.content !== RetrieveDescriptionMetaTagContent) {
                    this.meta.updateTag({ content: RetrieveDescriptionMetaTagContent }, 'name=description');
                }

            } else {
                if (currentDescriptionMetaTag?.content !== DefaultDescriptionMetaTagContent) {
                    this.meta.updateTag({ content: DefaultDescriptionMetaTagContent }, 'name=description');
                }
            }
        }

        if (currentTitle) {
            if (currentRoute.includes(routeNames.QUOTE_RETRIEVAL)) {
                if (currentTitle !== RetrieveTitle) {
                    this.title.setTitle(RetrieveTitle);
                }
            } else {
                if (currentTitle !== DefaultTitle) {
                    this.title.setTitle(DefaultTitle);
                }
            }
        }
    }

    // props to TrailBlazers 🙃
    /**
    * TRAIL-1996 Using popState to block browser forward navigation to avoid bypassing validation
    * https://stackoverflow.com/questions/11548558/html5-history-disabling-forward-button
    */
    private monitorPopstate(): void {
        //setup the popstate EventListener that reacts to browser history events
        window.addEventListener("popstate", function (event) {
            // In order to remove any "forward"-history (i.e. disable forward 
            // button), this popstate's event history state (having been navigated 
            // back to) must be insert _again_ as a new history state, thereby 
            // making it the new most forwad history state. 
            // This leaves the problem that to have this popstate event's history
            // state to become the new top, it would now be multiple in the history
            //
            // Effectively history would be:
            //  * [states before..] ->
            //  * [popstate's event.state] -> 
            //  * [*newly pushed _duplicate_ of popstate's event.state ]
            // 
            // To remove the annoyance/confusion for the user to have duplicate
            // history states, meaning it has to be clicked at least twice to go 
            // back, we pursue the strategy of marking the current history state
            // as "obsolete", as it has been inserted _again_ as to be the most
            // forward history entry. 
            // 
            // the popstate EventListener will hence have to distinguish 2 cases:
            //
            // case A) "popstate event is _not_ an obsolete duplicate"...
            if (typeof event.state == "object"
                && event.state?.obsolete !== true) {
                //...so we _firstly_ mark this state as to from now on "obsolete",
                // which can be done using the history API's replaceState method
                history.replaceState({ "obsolete": true }, "");
                // and _secondly_ push this state _one_more_time_ to the history
                // which will solve the OP's desired "disable forward button" issue
                history.pushState(event.state, "");
            }

            // case B: there is the other case that the user clicked "back" and
            // encounters one of the duplicate history event entries that are 
            // "obsolete" now.
            if (typeof event.state == "object"
                && event.state?.obsolete === true) {
                //...in which case we simply go "back" once more 
                history.back()
                // by this resolving the issue/problem that the user would
                // be counter-intuively needing to click back multiple times.
                // > we skip over the obsolete duplicates, that have been the
                // the result of necessarily pushing a history state to "disable
                // forward navigation"
            }

        }, false);
    }
}
